// EmanetX yasal sayfalari — qr.emanetx.com altinda servis edilir.
// Mobile WebView modal'i da bu sayfalari acar (in-app, kullaniciyi disari cikartmaz).
//
// 5 sayfa, ortak Cerceve:
//   /gizlilik-politikasi · /sozlesme · /cerez · /kvkk · /yasal
//
// Icerik referansi: rent.emanetx.com (aynisi, sadece iletisim bilgileri guncel).

import type { ReactNode } from 'react';

export const SIRKET_AD = 'Kuvve Teknoloji Limited Şirketi';
export const SIRKET_MARKA = 'EmanetX';
export const ADRES = 'Kaynarca Mh. Diyar Sk. No 6 / 1 Pendik / İstanbul - Türkiye';
export const EPOSTA = 'info@emanetx.com';
export const TELEFON = '+90 216 390 02 20';

const SON_GUNCELLEME = '21 Eylül 2025';

// === Ortak cerceve ===

function Cerceve({ baslik, alt, children }: { baslik: string; alt?: string; children: ReactNode }) {
  return (
    <main className="min-h-screen bg-zinc-50">
      <div className="mx-auto max-w-3xl px-6 py-12">
        <header className="border-b border-zinc-200 pb-6">
          <a
            href="/"
            className="inline-flex items-center text-sm font-bold text-zinc-500 hover:text-zinc-900"
          >
            ← emanet<span className="text-amber-500">x</span>
          </a>
          <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-zinc-900">{baslik}</h1>
          {alt && <p className="mt-1 text-sm text-zinc-500">{alt}</p>}
        </header>

        <article className="prose prose-zinc mt-8 max-w-none text-zinc-700">{children}</article>

        <footer className="mt-16 border-t border-zinc-200 pt-6 text-center text-xs text-zinc-400">
          © {new Date().getFullYear()} {SIRKET_AD}.<span className="px-2">·</span>
          <a className="hover:text-zinc-600" href={`mailto:${EPOSTA}`}>
            {EPOSTA}
          </a>
        </footer>
      </div>
    </main>
  );
}

// Yasal sayfa CSS sinifi (prose) icin yardimcilar
const baslikSinif = 'mt-8 text-xl font-extrabold text-zinc-900';
const paragrafSinif = 'mt-3 leading-relaxed';
const listeSinif = 'mt-3 list-disc space-y-1 pl-6';
const altListeSinif = 'mt-2 list-disc space-y-1 pl-6';

// === 1. Gizlilik Politikasi ===

export function GizlilikPolitikasi() {
  return (
    <Cerceve baslik="Gizlilik Politikası" alt={`Son Güncelleme: ${SON_GUNCELLEME}`}>
      <h2 className={baslikSinif}>1. Topladığımız Kişisel Veriler</h2>
      <p className={paragrafSinif}>Hizmetleri sunmak için şunları toplayabiliriz:</p>
      <ul className={listeSinif}>
        <li>
          <strong>Kimlik Bilgileri:</strong> Ad, soyadı, T.C. kimlik numarası, doğum tarihi
        </li>
        <li>
          <strong>İletişim Bilgileri:</strong> E-posta, telefon, adres
        </li>
        <li>
          <strong>Finansal Bilgiler:</strong> Kredi kartı ve banka hesap bilgileri (şifreli işlenir)
        </li>
        <li>
          <strong>İşlem Bilgileri:</strong> Kiralama geçmişi, tarihler, dolap konumları, ödeme
          kayıtları
        </li>
        <li>
          <strong>Teknik Bilgiler:</strong> IP adresi, tarayıcı türü, işletim sistemi, cihaz ve
          konum bilgileri
        </li>
        <li>
          <strong>Müşteri Desteği Bilgileri:</strong> Destek görüşmeleri, e-postalar, anket
          cevapları
        </li>
      </ul>

      <h2 className={baslikSinif}>2. Kişisel Verilerinizi Nasıl Kullanıyoruz?</h2>
      <p className={paragrafSinif}>Veriler aşağıdaki amaçlarla kullanılır:</p>
      <ul className={listeSinif}>
        <li>Hizmetleri sunmak ve geliştirmek</li>
        <li>Kiralama işlemleri ve rezervasyonlar</li>
        <li>Ödeme işlemleri</li>
        <li>İletişim ve müşteri desteği</li>
        <li>Hizmetlerimizi sizin tercihlerinize göre kişiselleştirmek</li>
        <li>Pazarlama faaliyetleri (rızanız dahilinde)</li>
        <li>Yasal yükümlülükler ve dolandırıcılık önleme</li>
        <li>Güvenlik iyileştirmeleri</li>
      </ul>

      <h2 className={baslikSinif}>3. Kişisel Verilerinizi Kimlerle Paylaşıyoruz?</h2>
      <p className={paragrafSinif}>Veriler şu taraflarla paylaşılabilir:</p>
      <ul className={listeSinif}>
        <li>
          <strong>Hizmet Sağlayıcılar:</strong> Ödeme işleme, analiz, depolama, pazarlama
        </li>
        <li>
          <strong>İş Ortakları:</strong> Stratejik ortaklar ve kampanya düzenleyicileri
        </li>
        <li>
          <strong>Yasal Merciler:</strong> Yasal uyum ve devlet talepleri
        </li>
        <li>
          <strong>Şirket Devri:</strong> Birleşme, devralma veya varlık satışı durumları
        </li>
        <li>
          <strong>İzninizle:</strong> Açık onayınız dahilinde
        </li>
      </ul>

      <h2 className={baslikSinif}>4. Kişisel Verilerinizin Güvenliği</h2>
      <p className={paragrafSinif}>Koruma önlemleri:</p>
      <ul className={listeSinif}>
        <li>SSL/TLS şifreleme</li>
        <li>Erişim kontrolleri ve yetkilendirme</li>
        <li>Güvenlik duvarları ve izinsiz giriş tespit sistemleri</li>
        <li>Düzenli güvenlik denetimleri</li>
        <li>Çalışan eğitimi</li>
      </ul>
      <p className={paragrafSinif}>
        Internet üzerinden hiçbir veri aktarımının veya elektronik depolama yönteminin %100 güvenli
        olmadığını belirtmek isteriz.
      </p>

      <h2 className={baslikSinif}>5. Kişisel Verileriniz Üzerindeki Haklarınız</h2>
      <p className={paragrafSinif}>6698 sayılı KVKK uyarınca sahip olduğunuz haklar:</p>
      <ul className={listeSinif}>
        <li>Verilerinizin işlenip işlenmediğini öğrenme</li>
        <li>İşleme amacı hakkında bilgi talep etme</li>
        <li>Verilerin aktarıldığı üçüncü kişileri bilme</li>
        <li>Eksik/yanlış verileri düzeltme</li>
        <li>Verilerin silinmesini isteme</li>
        <li>Otomatik analiz sonuçlarına itiraz etme</li>
        <li>Kanuna aykırı işleme nedeniyle zararın giderilmesini talep etme</li>
      </ul>
      <p className={paragrafSinif}>Bu haklarınızı kullanmak için iletişim kurun.</p>

      <h2 className={baslikSinif}>6. Çerez Politikası</h2>
      <p className={paragrafSinif}>
        Çerezler kullanıcı deneyimi, performans analizi ve pazarlama için kullanılır. Daha fazla
        bilgi için{' '}
        <a className="text-amber-600 underline" href="/cerez">
          Çerez Politikası
        </a>{' '}
        sayfasını ziyaret edin.
      </p>

      <h2 className={baslikSinif}>7. Değişiklikler</h2>
      <p className={paragrafSinif}>
        Politika değişiklikleri web sitesinde yayınlandığında yürürlüğe girer. Önemli değişiklikler
        hakkında bilgilendirileceksiniz.
      </p>

      <h2 className={baslikSinif}>8. İletişim</h2>
      <p className={paragrafSinif}>
        <strong>
          {SIRKET_AD} ({SIRKET_MARKA})
        </strong>
        <br />
        Adres: {ADRES}
        <br />
        E-posta:{' '}
        <a className="text-amber-600 underline" href={`mailto:${EPOSTA}`}>
          {EPOSTA}
        </a>
        <br />
        Telefon: {TELEFON}
      </p>
    </Cerceve>
  );
}

// === 2. Sozlesme ===

export function Sozlesme() {
  return (
    <Cerceve baslik="EmanetX Akıllı Dolap Kullanıcı Sözleşmesi" alt="Tam metin">
      <p className={paragrafSinif}>
        Bu sözleşme, EmanetX markası altında akıllı dolap kiralama hizmeti sunan {SIRKET_AD} (bundan
        sonra "Şirket" olarak anılacaktır) ile hizmetten faydalanan kişi (bundan sonra "Kullanıcı"
        olarak anılacaktır) arasındaki şartları ve koşulları düzenler.
      </p>

      <h2 className={baslikSinif}>1. Taraflar ve Tanımlar</h2>
      <p className={paragrafSinif}>
        <strong>Şirket:</strong> {SIRKET_AD}
      </p>
      <p className={paragrafSinif}>
        <strong>Kullanıcı:</strong> Şirkete ait akıllı dolapları kiralama hizmetinden faydalanan
        gerçek veya tüzel kişi.
      </p>
      <p className={paragrafSinif}>
        <strong>Hizmet:</strong> Şirketin belirlediği lokasyonlarda bulunan akıllı dolapların,
        belirlenen süre ve ücret karşılığında Kullanıcı'ya kiralanması.
      </p>

      <h2 className={baslikSinif}>2. Sözleşmenin Konusu</h2>
      <p className={paragrafSinif}>
        İşbu sözleşmenin konusu, Kullanıcı'nın Şirket'e ait EmanetX akıllı dolaplarını kullanarak
        kişisel eşyalarını geçici olarak saklamasına ilişkin hizmetin koşullarının ve tarafların hak
        ve yükümlülüklerinin belirlenmesidir.
      </p>

      <h2 className={baslikSinif}>3. Hizmet Kullanım Koşulları</h2>
      <p className={paragrafSinif}>
        Kullanıcı, mobil cihazı ile QR kodu okutarak veya ilgili arayüzü kullanarak hizmeti
        başlatır. Kullanıcı, seçtiği dolap ve kiralama süresine göre belirlenen ücreti, sunulan
        ödeme yöntemleriyle peşin olarak öder. Ödeme başarılı olduğunda ilgili dolabın kilidi
        otomatik olarak açılır. Kullanıcı, eşyasını yerleştirdikten sonra dolap kapağını tam olarak
        kapatmakla yükümlüdür. Kapağın kapandığından ve kilidin devreye girdiğinden emin olmalıdır.
      </p>

      <h2 className={baslikSinif}>4. Ücretlendirme, Süre Aşımı ve Unutulan Eşyalar</h2>
      <p className={paragrafSinif}>
        Kiralama ücreti, seçilen süreye göre peşin alınır. Süre sonunda eşyasını almayan Kullanıcı
        için ek ücretlendirme uygulanır. Ek ücret ödenmeden dolap açılmaz. Kiralanan dolaptaki
        eşyaların 15 (on beş) gün içinde alınmaması durumunda, Şirket dolabı boşaltma hakkına
        sahiptir. Boşaltılan eşyalar, Şirket'in belirleyeceği bir merkezde 30 (otuz) gün süreyle
        saklanır. Bu süre sonunda da alınmayan eşyalar, Şirket tarafından imha edilebilir veya bir
        hayır kurumuna bağışlanabilir. Kullanıcı, unutulan eşyalar üzerinde hiçbir hak iddia edemez.
      </p>

      <h2 className={baslikSinif}>5. İptal, İade ve Kredi Kartı Güvenliği</h2>
      <p className={paragrafSinif}>
        <strong>İptal ve İade:</strong> Sunulan hizmet, doğası gereği "anında ifa edilen" bir
        hizmettir. Ödeme işlemi başarılı bir şekilde tamamlanıp dolap kilidi açıldığı anda hizmet
        başlamış sayılır ve seçilen kiralama periyodu için ücret iadesi yapılmaz. Kullanıcının
        dolabı kullanmaktan vazgeçmesi veya eşya yerleştirmemesi durumu iade hakkı doğurmaz, zira
        ilgili dolap o süre boyunca Kullanıcı'ya tahsis edilmiştir.
      </p>
      <p className={paragrafSinif}>
        <strong>Kredi Kartı Güvenliği:</strong> Kullanıcının ödeme esnasında verdiği kredi kartı
        bilgileri, Şirket sunucularında kesinlikle saklanmaz. Tüm ödeme işlemleri, SSL korumalı ve
        PCI-DSS uyumlu, lisanslı üçüncü parti ödeme hizmeti sağlayıcıları aracılığıyla güvenli bir
        altyapıda gerçekleştirilir. Kart bilgilerinin güvenliğinden Şirket sorumlu tutulamaz.
      </p>

      <h2 className={baslikSinif}>6. Yasaklanmış ve İzin Verilmeyen Emanetler</h2>
      <p className={paragrafSinif}>
        Kullanıcı, akıllı dolaplara aşağıda listelenen maddeleri veya benzerlerini kesinlikle
        bırakmayacağını kabul ve taahhüt eder. Bu maddelerin bırakılması sonucu doğabilecek her
        türlü hukuki, cezai ve mali sorumluluk tamamen Kullanıcı'ya aittir. Şirket, şüpheli
        durumlarda yasal mercilere haber verme ve dolabı yetkililer huzurunda açma hakkını saklı
        tutar.
      </p>
      <h3 className="mt-5 font-extrabold text-zinc-900">1. Yasa Dışı Maddeler</h3>
      <p className={paragrafSinif}>
        Her türlü uyuşturucu, ateşli silah, patlayıcı, çalıntı mal ve yasa dışı kabul edilen tüm
        materyaller.
      </p>
      <h3 className="mt-5 font-extrabold text-zinc-900">2. Tehlikeli ve Zararlı Maddeler</h3>
      <p className={paragrafSinif}>
        Yanıcı, parlayıcı, aşındırıcı, zehirli, radyoaktif ve kimyasal maddeler. Basınçlı kaplar
        (tüp, sprey vb.).
      </p>
      <h3 className="mt-5 font-extrabold text-zinc-900">3. Canlı Varlıklar ve Gıda Ürünleri</h3>
      <p className={paragrafSinif}>
        Hayvan, bitki gibi canlı varlıklar. Kolay bozulabilen, koku yapabilecek her türlü yiyecek ve
        içecek.
      </p>
      <h3 className="mt-5 font-extrabold text-zinc-900">4. Çok Değerli Eşyalar</h3>
      <p className={paragrafSinif}>
        Nakit para, döviz, mücevher, değerli maden, hisse senedi, tahvil gibi menkul kıymetler. (Bu
        tür eşyaların kaybından Şirket sorumlu tutulamaz.)
      </p>
      <h3 className="mt-5 font-extrabold text-zinc-900">5. Hassas Belgeler</h3>
      <p className={paragrafSinif}>
        Pasaport, kimlik kartı, resmi evrak gibi kişiye özel ve yeniden temini zor olan belgeler.
      </p>

      <h2 className={baslikSinif}>7. Sorumluluğun Sınırlandırılması</h2>
      <p className={paragrafSinif}>
        Şirket, dolapların fiziki güvenliği için gerekli tedbirleri alır ancak bir sigorta şirketi
        değildir. Dolaplara bırakılan eşyaların içeriğinden, değerinden veya uğrayabileceği
        zararlardan (çizilme, kırılma vb.) Şirket sorumlu değildir. Şirketin sorumluluğu, ağır
        kusuru kanıtlanmadığı sürece, yalnızca alınan hizmet bedeli ile sınırlıdır. Mücbir sebepler
        (deprem, sel, yangın, terör, siber saldırı vb.) nedeniyle oluşabilecek zararlardan Şirket
        sorumlu tutulamaz.
      </p>

      <h2 className={baslikSinif}>8. Gizlilik ve Kişisel Verilerin Korunması</h2>
      <p className={paragrafSinif}>
        Şirket, Kullanıcı'nın kişisel verilerinin gizliliğine önem verir. Kişisel verilerin
        toplanması, işlenmesi ve korunmasına ilişkin tüm detaylar, işbu sözleşmenin ayrılmaz bir
        parçası olan{' '}
        <a className="text-amber-600 underline" href="/kvkk">
          KVKK Aydınlatma Metni
        </a>
        'nde açıklanmıştır. Kullanıcı, hizmeti kullanarak bu metni okuduğunu ve anladığını kabul
        eder.
      </p>

      <h2 className={baslikSinif}>9. Fikri Mülkiyet Hakları</h2>
      <p className={paragrafSinif}>
        "EmanetX" markası, logosu, web sitesi, mobil arayüzleri, yazılımları ve diğer tüm
        bileşenleri {SIRKET_AD}'ne aittir ve fikri mülkiyet hukuku kapsamında korunmaktadır.
        Kullanıcı, bu varlıkları Şirket'in yazılı izni olmaksızın kopyalayamaz, çoğaltamaz veya
        kullanamaz.
      </p>

      <h2 className={baslikSinif}>10. Sözleşme Değişiklikleri</h2>
      <p className={paragrafSinif}>
        Şirket, işbu sözleşmeyi dilediği zaman tek taraflı olarak güncelleme hakkını saklı tutar.
        Değişiklikler, web sitesinde veya ilgili platformda yayınlandığı an itibarıyla yürürlüğe
        girer. Hizmeti kullanmaya devam eden Kullanıcı, güncellenmiş sözleşme şartlarını kabul etmiş
        sayılır.
      </p>

      <h2 className={baslikSinif}>11. Delil Sözleşmesi</h2>
      <p className={paragrafSinif}>
        Taraflar arasında doğabilecek her türlü uyuşmazlıkta, Şirket'in ticari defter, kayıt ve
        belgeleri ile bilgisayar, sunucu kayıtları ve her türlü dijital verisi, Hukuk Muhakemeleri
        Kanunu'nun 193. maddesi uyarınca bağlayıcı, kesin ve münhasır delil teşkil edecektir.
      </p>

      <h2 className={baslikSinif}>12. Yetkili Mahkeme ve Uygulanacak Hukuk</h2>
      <p className={paragrafSinif}>
        İşbu sözleşmeden doğabilecek her türlü uyuşmazlığın çözümünde Türkiye Cumhuriyeti kanunları
        uygulanacak olup, İstanbul Anadolu Mahkemeleri ve İcra Daireleri yetkilidir.
      </p>

      <p className="mt-10 text-sm text-zinc-500">
        Kullanıcı, hizmeti kullanarak ve ödeme yaparak bu sözleşmenin tüm şartlarını okuduğunu,
        anladığını ve kabul ettiğini beyan eder.
      </p>
    </Cerceve>
  );
}

// === 3. Cerez Politikasi ===

export function CerezPolitikasi() {
  return (
    <Cerceve baslik="Çerez Politikası" alt={`Son Güncelleme: ${SON_GUNCELLEME}`}>
      <p className={paragrafSinif}>
        Bu Çerez Politikası, <strong>{SIRKET_AD}</strong> ("Şirket", "biz", "bize" veya "bizim")
        tarafından işletilen{' '}
        <a className="text-amber-600 underline" href="https://qr.emanetx.com">
          qr.emanetx.com
        </a>{' '}
        ve mobil uygulamaları ("Hizmetler") aracılığıyla çerezlerin ve benzeri teknolojilerin nasıl
        kullanıldığını açıklamaktadır. Hizmetlerimizi kullanarak, bu Çerez Politikası'nda belirtilen
        çerez kullanımını kabul etmiş olursunuz.
      </p>

      <h2 className={baslikSinif}>1. Çerez (Cookie) Nedir?</h2>
      <p className={paragrafSinif}>
        Çerezler, ziyaret ettiğiniz web siteleri tarafından bilgisayarınıza, cep telefonunuza veya
        diğer cihazlarınıza depolanan küçük metin dosyalarıdır. Bu dosyalar, web sitesini tekrar
        ziyaret ettiğinizde cihazınızı tanımak ve siteyi daha verimli kullanmanızı sağlamak için
        kullanılır.
      </p>

      <h2 className={baslikSinif}>2. Çerezleri Neden Kullanıyoruz?</h2>
      <ul className={listeSinif}>
        <li>
          <strong>Hizmetlerin Çalışmasını Sağlamak:</strong> Web sitesinin temel fonksiyonlarını
          yerine getirmek.
        </li>
        <li>
          <strong>Deneyiminizi Kişiselleştirmek:</strong> Tercihlerinizi hatırlamak.
        </li>
        <li>
          <strong>Hizmet Performansını Analiz Etmek:</strong> Web sitesinin nasıl kullanıldığını
          anlamak.
        </li>
        <li>
          <strong>Pazarlama ve Reklam Faaliyetleri:</strong> İlgi alanlarınıza uygun reklamlar
          göstermek.
        </li>
        <li>
          <strong>Güvenliği Sağlamak:</strong> Yetkisiz erişimi önlemek.
        </li>
      </ul>

      <h2 className={baslikSinif}>3. Kullandığımız Çerez Türleri</h2>
      <h3 className="mt-5 font-extrabold text-zinc-900">3.1. Zorunlu Çerezler</h3>
      <p className={paragrafSinif}>
        Web sitesinin düzgün çalışması için kesinlikle gereklidir. Örneğin, oturum açma bilgilerini
        hatırlamak.
      </p>
      <h3 className="mt-5 font-extrabold text-zinc-900">3.2. Performans ve Analitik Çerezler</h3>
      <p className={paragrafSinif}>
        Web sitemizin nasıl kullanıldığını anlamamıza yardımcı olur. Bu çerezlerin topladığı
        bilgiler anonimdir.
      </p>
      <h3 className="mt-5 font-extrabold text-zinc-900">3.3. Fonksiyonel Çerezler</h3>
      <p className={paragrafSinif}>
        Dil tercihi, konum ayarları gibi seçimlerinizi hatırlayarak size daha uygun içerikler sunar.
      </p>
      <h3 className="mt-5 font-extrabold text-zinc-900">3.4. Pazarlama ve Hedefleme Çerezleri</h3>
      <p className={paragrafSinif}>
        İlgi alanlarınıza daha uygun reklamlar sunmak için kullanılır.
      </p>

      <h2 className={baslikSinif}>4. Çerezleri Nasıl Yönetebilirsiniz?</h2>
      <p className={paragrafSinif}>
        Çerez tercihlerinizi yönetmek tarayıcı ayarlarınız üzerinden mümkündür. Çoğu internet
        tarayıcısı çerezleri otomatik olarak kabul eder, ancak bu ayarı değiştirebilirsiniz.
      </p>
      <ul className={listeSinif}>
        <li>
          <strong>Google Chrome:</strong> Ayarlar → Gizlilik ve Güvenlik → Çerezler ve Diğer Site
          Verileri
        </li>
        <li>
          <strong>Mozilla Firefox:</strong> Seçenekler → Gizlilik ve Güvenlik → Çerezler ve Site
          Verileri
        </li>
        <li>
          <strong>Microsoft Edge:</strong> Ayarlar → Gizlilik, Arama ve Hizmetler → Çerezler ve Site
          İzinleri
        </li>
        <li>
          <strong>Safari:</strong> Tercihler → Gizlilik
        </li>
      </ul>

      <h2 className={baslikSinif}>5. Üçüncü Taraf Çerezleri</h2>
      <p className={paragrafSinif}>
        Hizmetlerimizi kullanırken üçüncü taraf hizmet sağlayıcılar (Google Analytics, sosyal medya
        eklentileri, reklam ortakları) tarafından çerezler yerleştirilebilir. Bu çerezlerin
        kullanımından üçüncü tarafların kendi gizlilik politikaları sorumludur.
      </p>

      <h2 className={baslikSinif}>6. Değişiklikler</h2>
      <p className={paragrafSinif}>
        Bu Çerez Politikası'nı zaman zaman güncelleyebiliriz. Politikadaki değişiklikler web
        sitemizde yayınlandığı tarihte yürürlüğe girer.
      </p>

      <h2 className={baslikSinif}>7. İletişim</h2>
      <p className={paragrafSinif}>
        <strong>
          {SIRKET_AD} ({SIRKET_MARKA})
        </strong>
        <br />
        E-posta:{' '}
        <a className="text-amber-600 underline" href={`mailto:${EPOSTA}`}>
          {EPOSTA}
        </a>
      </p>
    </Cerceve>
  );
}

// === 4. KVKK Aydinlatma Metni ===

export function Kvkk() {
  return (
    <Cerceve baslik="KVKK Aydınlatma Metni" alt="6698 sayılı Kişisel Verilerin Korunması Kanunu">
      <p className={paragrafSinif}>
        Bu aydınlatma metni, 6698 sayılı Kişisel Verilerin Korunması Kanunu ("KVKK") uyarınca, veri
        sorumlusu sıfatıyla {SIRKET_AD} ("Şirket") tarafından, kişisel verilerinizin işlenmesine
        ilişkin olarak sizleri bilgilendirmek amacıyla hazırlanmıştır.
      </p>

      <h2 className={baslikSinif}>1. Veri Sorumlusu</h2>
      <p className={paragrafSinif}>
        Kişisel verileriniz, veri sorumlusu olarak {SIRKET_AD} tarafından aşağıda açıklanan kapsamda
        işlenebilecektir.
      </p>

      <h2 className={baslikSinif}>2. Kişisel Verilerin İşlenme Amaçları</h2>
      <p className={paragrafSinif}>
        Toplanan kişisel verileriniz, Şirketimiz tarafından sunulan hizmetlerden sizleri
        faydalandırmak için gerekli çalışmaların yapılması amacıyla KVKK'nın 5. ve 6. maddelerinde
        belirtilen kişisel veri işleme şartları dahilinde işlenecektir. Bu kapsamda verileriniz;
      </p>
      <ul className={listeSinif}>
        <li>Akıllı dolap kiralama hizmetinin sunulması ve yönetilmesi,</li>
        <li>Kullanıcı kimliğinin doğrulanması (SMS ile),</li>
        <li>Hizmet bedelinin tahsilatı ve finansal işlemlerin yürütülmesi,</li>
        <li>Hizmetin güvenliğinin sağlanması ve olası sahtekarlıkların önlenmesi,</li>
        <li>Gerekli durumlarda sizinle iletişim kurulması,</li>
        <li>Yasal yükümlülüklerin yerine getirilmesi,</li>
        <li>Hizmet kalitesinin artırılmasına yönelik analizler yapılması</li>
      </ul>
      <p className={paragrafSinif}>amaçlarıyla işlenmektedir.</p>

      <h2 className={baslikSinif}>3. İşlenen Kişisel Veriler</h2>
      <p className={paragrafSinif}>
        Hizmetimiz kapsamında sizden toplanan kişisel veriler şunlardır:
      </p>
      <ul className={listeSinif}>
        <li>
          <strong>İletişim Verisi:</strong> Cep telefonu numarası.
        </li>
        <li>
          <strong>Finansal Veri:</strong> Ödeme bilgileri (Kredi kartı bilgileri tarafımızca
          saklanmamakta, anlaşmalı lisanslı ödeme hizmeti sağlayıcısı tarafından işlenmektedir).
        </li>
        <li>
          <strong>İşlem Güvenliği Verisi:</strong> İşlem zamanı, kullanılan dolap numarası, lokasyon
          bilgisi, IP adresi.
        </li>
      </ul>

      <h2 className={baslikSinif}>4. Kişisel Verilerin Aktarılması</h2>
      <p className={paragrafSinif}>
        Kişisel verileriniz, yasal düzenlemelerin öngördüğü kapsamda, hizmetin yerine
        getirilebilmesi amacıyla; anlaşmalı olduğumuz ödeme hizmeti sağlayıcılarına, SMS gönderim
        hizmeti sunan iş ortaklarımıza ve talep edilmesi halinde kanunen yetkili kamu kurumları ve
        özel kişilerle paylaşılabilecektir.
      </p>

      <h2 className={baslikSinif}>5. Kişisel Veri Toplamanın Yöntemi ve Hukuki Sebebi</h2>
      <p className={paragrafSinif}>
        Kişisel verileriniz, hizmetimizi kullanmanız sırasında elektronik ortamda (QR kod okutma,
        web arayüzü vb.) otomatik yollarla toplanmaktadır. Bu verilerin işlenmesinin hukuki sebebi,
        KVKK'nın 5. maddesinde belirtilen "bir sözleşmenin kurulması veya ifasıyla doğrudan doğruya
        ilgili olması kaydıyla, sözleşmenin taraflarına ait kişisel verilerin işlenmesinin gerekli
        olması" ve "veri sorumlusunun hukuki yükümlülüğünü yerine getirebilmesi için zorunlu olması"
        şartlarına dayanmaktadır.
      </p>

      <h2 className={baslikSinif}>6. İlgili Kişinin Hakları (KVKK Madde 11)</h2>
      <p className={paragrafSinif}>KVKK'nın 11. maddesi uyarınca aşağıdaki haklara sahipsiniz:</p>
      <ul className={listeSinif}>
        <li>Kişisel verilerinizin işlenip işlenmediğini öğrenme,</li>
        <li>İşlenmişse buna ilişkin bilgi talep etme,</li>
        <li>İşlenme amacını ve amacına uygun kullanılıp kullanılmadığını öğrenme,</li>
        <li>Yurt içinde veya yurt dışında verilerin aktarıldığı üçüncü kişileri bilme,</li>
        <li>Verilerin eksik veya yanlış işlenmiş olması hâlinde düzeltilmesini isteme,</li>
        <li>
          KVKK'nın 7. maddesinde öngörülen şartlar çerçevesinde kişisel verilerin silinmesini veya
          yok edilmesini isteme,
        </li>
        <li>
          Yapılan işlemlerin, kişisel verilerin aktarıldığı üçüncü kişilere bildirilmesini isteme,
        </li>
        <li>
          İşlenen verilerin münhasıran otomatik sistemler vasıtasıyla analiz edilmesi suretiyle
          aleyhinize bir sonucun ortaya çıkmasına itiraz etme,
        </li>
        <li>
          Kişisel verilerin kanuna aykırı olarak işlenmesi sebebiyle zarara uğramanız hâlinde
          zararın giderilmesini talep etme.
        </li>
      </ul>
      <p className={paragrafSinif}>
        Yukarıda belirtilen haklarınızı kullanmak için taleplerinizi{' '}
        <a className="text-amber-600 underline" href={`mailto:${EPOSTA}`}>
          {EPOSTA}
        </a>{' '}
        e-posta adresine iletebilirsiniz.
      </p>

      <h2 className={baslikSinif}>7. İletişim</h2>
      <p className={paragrafSinif}>
        <strong>{SIRKET_AD}</strong>
        <br />
        Adres: {ADRES}
        <br />
        E-posta:{' '}
        <a className="text-amber-600 underline" href={`mailto:${EPOSTA}`}>
          {EPOSTA}
        </a>
        <br />
        Telefon: {TELEFON}
      </p>
    </Cerceve>
  );
}

// === 5. Yasal Bilgilendirme ===

export function YasalBilgilendirme() {
  return (
    <Cerceve baslik="Yasal Bilgilendirme" alt={`Son Güncelleme: ${SON_GUNCELLEME}`}>
      <p className={paragrafSinif}>
        Bu Yasal Bilgilendirme sayfası, <strong>{SIRKET_AD}</strong> tarafından işletilen{' '}
        <a className="text-amber-600 underline" href="https://qr.emanetx.com">
          qr.emanetx.com
        </a>{' '}
        ve hizmetlerinin ("Hizmetler") kullanımına ilişkin genel hukuki çerçeveyi sunmaktadır.
        Hizmetlerimizi kullanarak aşağıdaki koşulları kabul etmiş sayılırsınız.
      </p>

      <h2 className={baslikSinif}>1. Hizmetlerin Sağlayıcısı</h2>
      <p className={paragrafSinif}>
        EmanetX hizmetleri, Türkiye Cumhuriyeti kanunlarına uygun olarak kurulmuş ve faaliyet
        gösteren <strong>{SIRKET_AD}</strong> tarafından sağlanmaktadır.
      </p>
      <p className={paragrafSinif}>
        <strong>Şirket Adı:</strong> {SIRKET_AD}
        <br />
        <strong>Adres:</strong> {ADRES}
        <br />
        <strong>E-posta:</strong>{' '}
        <a className="text-amber-600 underline" href={`mailto:${EPOSTA}`}>
          {EPOSTA}
        </a>
        <br />
        <strong>Telefon:</strong> {TELEFON}
      </p>

      <h2 className={baslikSinif}>2. Fikri Mülkiyet Hakları</h2>
      <p className={paragrafSinif}>
        EmanetX markası, logosu, web sitesi tasarımı, metinleri, görselleri, yazılımları ve tüm
        diğer içerikleri <strong>{SIRKET_AD}</strong>'ne aittir veya kullanım lisansı altındadır. Bu
        içerikler, Fikri ve Sınai Haklar Kanunları kapsamında korunmaktadır.
      </p>
      <ul className={listeSinif}>
        <li>Tüm yazılımlar, tasarım ve kodlar.</li>
        <li>Web sitesinde yer alan tüm görsel ve işitsel materyaller.</li>
        <li>Marka isimleri, logolar ve ticari unvanlar.</li>
        <li>Metinler ve bilgi içerikleri.</li>
      </ul>

      <h2 className={baslikSinif}>3. Sorumluluk Reddi</h2>
      <p className={paragrafSinif}>
        Hizmetlerimizde sunulan tüm bilgi ve içerikler genel bilgilendirme amacı taşımaktadır.{' '}
        <strong>{SIRKET_AD}</strong>, web sitesindeki bilgilerin doğruluğu, eksiksizliği veya
        güncelliği konusunda herhangi bir garanti vermemektedir.
      </p>
      <ul className={listeSinif}>
        <li>Hizmetlerin kesintisiz veya hatasız çalışacağına dair garanti verilmez.</li>
        <li>
          Üçüncü taraf bağlantılarından (linklerden) kaynaklanabilecek içerik veya zararlardan
          Şirketimiz sorumlu değildir.
        </li>
        <li>
          Kullanıcıların kendi hatalarından veya ihmallerinden kaynaklanan zararlardan Şirketimiz
          sorumlu tutulamaz.
        </li>
      </ul>

      <h2 className={baslikSinif}>4. Kullanım Koşulları</h2>
      <p className={paragrafSinif}>
        Hizmetlerimizin kullanımı,{' '}
        <a className="text-amber-600 underline" href="/sozlesme">
          Kullanıcı Sözleşmesi
        </a>
        'nde belirtilen hüküm ve koşullara tabidir. Hizmetlerimizi kullanarak, Kullanıcı
        Sözleşmesi'ni okuduğunuzu, anladığınızı ve kabul ettiğinizi beyan etmiş olursunuz.
      </p>

      <h2 className={baslikSinif}>5. Kişisel Verilerin Korunması</h2>
      <p className={paragrafSinif}>
        Kişisel verilerinizin toplanması, işlenmesi ve korunması ile ilgili detaylı bilgi için
        lütfen{' '}
        <a className="text-amber-600 underline" href="/gizlilik-politikasi">
          Gizlilik Politikası
        </a>{' '}
        ve{' '}
        <a className="text-amber-600 underline" href="/kvkk">
          KVKK Aydınlatma Metni
        </a>{' '}
        sayfalarımızı inceleyiniz.
      </p>

      <h2 className={baslikSinif}>6. Uygulanacak Hukuk ve Yetkili Mahkeme</h2>
      <p className={paragrafSinif}>
        Bu Yasal Bilgilendirme ve Hizmetlerin kullanımından doğacak her türlü ihtilaf, Türkiye
        Cumhuriyeti kanunlarına tabi olacaktır. İhtilafların çözümünde İstanbul Anadolu Mahkemeleri
        ve İcra Daireleri yetkilidir.
      </p>

      <h2 className={baslikSinif}>7. Değişiklikler</h2>
      <p className={paragrafSinif}>
        Bu Yasal Bilgilendirme metni, <strong>{SIRKET_AD}</strong> tarafından önceden bildirimde
        bulunmaksızın güncellenebilir veya değiştirilebilir. Yapılan değişiklikler web sitemizde
        yayınlandığı tarihte yürürlüğe girer.
      </p>

      <h2 className={baslikSinif}>İletişim</h2>
      <p className={paragrafSinif}>
        <strong>
          {SIRKET_AD} ({SIRKET_MARKA})
        </strong>
        <br />
        Adres: {ADRES}
        <br />
        E-posta:{' '}
        <a className="text-amber-600 underline" href={`mailto:${EPOSTA}`}>
          {EPOSTA}
        </a>
        <br />
        Telefon: {TELEFON}
      </p>
    </Cerceve>
  );
}

// === Router yardimcisi ===

/** Yola gore yasal sayfayi dondurur; eslesmezse null. */
export function YasalRota({ yol }: { yol: string }): ReactNode | null {
  switch (yol) {
    case '/gizlilik-politikasi':
      return <GizlilikPolitikasi />;
    case '/sozlesme':
      return <Sozlesme />;
    case '/cerez':
      return <CerezPolitikasi />;
    case '/kvkk':
      return <Kvkk />;
    case '/yasal':
      return <YasalBilgilendirme />;
    default:
      return null;
  }
}
